import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Search, Edit2, Trash2, Package, 
  Check, X, Percent
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bundleTypeColors = {
  publication_bundle: 'bg-blue-100 text-blue-700',
  press_release_bundle: 'bg-green-100 text-green-700',
  social_bundle: 'bg-purple-100 text-purple-700',
  print_bundle: 'bg-amber-100 text-amber-700',
  cross_service: 'bg-[#D4AF37]/10 text-[#D4AF37]',
};

const AdminBundles = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, bundle: null });

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/bundles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBundles(response.data);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.bundle) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/bundles/${deleteModal.bundle.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bundle deleted');
      setBundles(bundles.filter(b => b.id !== deleteModal.bundle.id));
      setDeleteModal({ open: false, bundle: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleActive = async (bundle) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/bundles/${bundle.id}`, 
        { is_active: !bundle.is_active },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setBundles(bundles.map(b => 
        b.id === bundle.id ? { ...b, is_active: !b.is_active } : b
      ));
      toast.success(`Bundle ${bundle.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredBundles = bundles.filter(bundle => {
    const matchesSearch = bundle.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || bundle.bundle_type === filterType;
    return matchesSearch && matchesType;
  });

  const bundleTypes = [
    { value: 'publication_bundle', label: 'Publication Bundle' },
    { value: 'press_release_bundle', label: 'Press Release' },
    { value: 'social_bundle', label: 'Social Bundle' },
    { value: 'print_bundle', label: 'Print Bundle' },
    { value: 'cross_service', label: 'Cross Service' },
  ];

  return (
    <div className="p-6" data-testid="admin-bundles">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Bundles</h1>
          <p className="text-sm text-gray-500">Manage combo packages, press releases, and bundle deals</p>
        </div>
        <Link
          to="/admin/bundles/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] transition-colors"
          data-testid="add-bundle-btn"
        >
          <Plus className="w-4 h-4" />
          Create Bundle
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search bundles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
        >
          <option value="">All Types</option>
          {bundleTypes.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {bundleTypes.map(type => {
          const count = bundles.filter(b => b.bundle_type === type.value).length;
          return (
            <div key={type.value} className="bg-white border border-gray-100 rounded-lg p-4">
              <p className="text-2xl font-bold text-[#1a1a1a]">{count}</p>
              <p className="text-xs text-gray-500">{type.label}</p>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredBundles.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">No Bundles</h3>
            <p className="text-gray-500 mb-4">Create your first bundle package</p>
            <Link
              to="/admin/bundles/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Create Bundle
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Bundle</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Price</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Discount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Items</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredBundles.map(bundle => (
                <tr key={bundle.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {bundle.image ? (
                        <img src={bundle.image} alt={bundle.name} className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-[#1a1a1a] flex items-center justify-center">
                          <Package className="w-5 h-5 text-white/50" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-[#1a1a1a]">{bundle.name}</p>
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">{bundle.short_description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${bundleTypeColors[bundle.bundle_type] || 'bg-gray-100 text-gray-600'}`}>
                      {bundle.bundle_type?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      {bundle.original_price !== bundle.bundle_price && (
                        <span className="text-xs text-gray-400 line-through block">
                          ₹{bundle.original_price?.toLocaleString()}
                        </span>
                      )}
                      <span className="font-bold text-[#D4AF37]">₹{bundle.bundle_price?.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {bundle.discount_percent > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                        <Percent className="w-3 h-3" />
                        {bundle.discount_percent}% OFF
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium">{bundle.items?.length || 0} items</span>
                    {bundle.guaranteed_links && (
                      <span className="text-xs text-gray-500 block">{bundle.guaranteed_links}+ links</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(bundle)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        bundle.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {bundle.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {bundle.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/bundles/${bundle.id}/edit`}
                        className="p-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => setDeleteModal({ open: true, bundle })}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">Delete Bundle</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteModal.bundle?.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ open: false, bundle: null })}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBundles;
